
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDistanceToNow } from 'date-fns';
import { Check, X, User } from "lucide-react";

interface PendingMember {
  id: string;
  full_name: string;
  phone_number: string;
  email: string;
  state: string;
  local_government: string;
  created_at: string;
  age_group?: string;
  gender?: string;
  sub_group?: string;
}

const PendingMembersSection = () => {
  const [pendingMembers, setPendingMembers] = useState<PendingMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingActions, setProcessingActions] = useState<Record<string, boolean>>({});

  const fetchPendingMembers = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[PendingMembers] Fetching pending members from members table...');
      
      const { data, error } = await supabase
        .from('members')
        .select('id, full_name, phone_number, email, state, local_government, created_at, age_group, gender, sub_group')
        .eq('approved', false)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[PendingMembers] Error fetching pending members:', error);
        throw error;
      }
      
      console.log('[PendingMembers] Successfully fetched pending members:', data?.length || 0);
      setPendingMembers(data || []);
    } catch (error) {
      console.error('[PendingMembers] Error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load pending members."
      });
      setPendingMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingMembers();
  }, [fetchPendingMembers]);

  const approveMember = async (memberId: string) => {
    setProcessingActions(prev => ({ ...prev, [memberId]: true }));
    
    try {
      console.log("[PendingMembers] Approving member:", memberId);

      const { error: updateError } = await supabase
        .from('members')
        .update({ approved: true })
        .eq('id', memberId);

      if (updateError) {
        console.error('[PendingMembers] Error updating member status:', updateError);
        toast({
          variant: "destructive", 
          title: "Error",
          description: `Failed to approve member: ${updateError.message}`
        });
        return;
      }

      toast({
        title: "Success",
        description: "Member approved successfully!"
      });
      
      fetchPendingMembers();
    } catch (error) {
      console.error('[PendingMembers] Unexpected error approving member:', error);
      toast({
        variant: "destructive",
        title: "Error", 
        description: "An unexpected error occurred while approving member. Please try again."
      });
    } finally {
      setProcessingActions(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const rejectMember = async (memberId: string) => {
    setProcessingActions(prev => ({ ...prev, [memberId]: true }));
    
    try {
      console.log("[PendingMembers] Rejecting member:", memberId);
      
      const { error } = await supabase
        .from('members')
        .delete()
        .eq('id', memberId);

      if (error) {
        console.error('[PendingMembers] Error rejecting member:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to reject member: ${error.message}`
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "Member rejected and removed."
      });
      fetchPendingMembers();
    } catch (error) {
      console.error('[PendingMembers] Unexpected error rejecting member:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred while rejecting member. Please try again."
      });
    } finally {
      setProcessingActions(prev => ({ ...prev, [memberId]: false }));
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pending Member Approvals</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <div className="w-8 h-8 border-2 border-omoduduwa-indigo border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-gray-500">Loading pending approvals...</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (pendingMembers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pending Member Approvals (0)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <User className="mx-auto h-12 w-12 text-gray-300 mb-2" />
            <p className="text-gray-500">No pending approvals</p>
            <p className="text-gray-400 text-sm mt-2">
              New registrations will appear here for approval
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Member Approvals ({pendingMembers.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">{member.full_name}</TableCell>
                  <TableCell>
                    <div>{member.phone_number}</div>
                    <div className="text-xs text-gray-500">{member.email}</div>
                  </TableCell>
                  <TableCell>
                    <div>{member.state}</div>
                    <div className="text-xs text-gray-500">{member.local_government}</div>
                  </TableCell>
                  <TableCell>
                    {member.age_group && <div className="text-xs">Age: {member.age_group}</div>}
                    {member.gender && <div className="text-xs">Gender: {member.gender}</div>}
                    {member.sub_group && <div className="text-xs">Group: {member.sub_group}</div>}
                  </TableCell>
                  <TableCell>
                    {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-green-500 hover:bg-green-100 text-green-600"
                      onClick={() => approveMember(member.id)}
                      disabled={processingActions[member.id]}
                    >
                      <Check className="w-4 h-4 mr-1" /> 
                      {processingActions[member.id] ? 'Processing...' : 'Approve'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500 hover:bg-red-100 text-red-600"
                      onClick={() => rejectMember(member.id)}
                      disabled={processingActions[member.id]}
                    >
                      <X className="w-4 h-4 mr-1" /> 
                      {processingActions[member.id] ? 'Processing...' : 'Reject'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingMembersSection;
